// @givmo/app-admin — the company giving-publication read/write, addressed by URL (CW-WEB4).
//
// ══ WHY THIS FILE EXISTS ══════════════════════════════════════════════════════════════════════
// CW-BE6 landed `GET`/`PUT /admin/company/giving-publication` on the backend AFTER the committed
// OpenAPI snapshot (`packages/api-client/openapi.json`) was last refreshed, so there is no generated
// SDK operation, no generated type and no TanStack Query option factory for either route.
// `agent_docs/api-client.md` § *A whole ROUTE the snapshot lacks* is the sanctioned disposition, and
// it is deliberately NOT "refresh the snapshot for one feature": a full refresh absorbs every
// unrelated backend change since the last regen and collides with any regen already in flight.
//
// `givmoClient` is the same `@hey-api/client-fetch` client the generated operations bind to, so a
// call made here inherits the whole interceptor stack — Bearer, App Check, `X-Request-Id` (`wb-` on
// this app), the `{data,meta,links}` envelope unwrap, and the `{errors:[…]}` → `GivmoError` mapping.
// Nothing about the request differs from a generated one except who wrote the URL.
//
// ══ WHAT A 404 MEANS HERE ═════════════════════════════════════════════════════════════════════
// ⚠ NOT "no publication". The GET returns the UNPUBLISHED ZERO-STATE for a company that has never
// saved, so a 404 on this route means the SURFACE is off: CW-BE6 raises
// `CompanyGivingPublicationDisabledException` while the backend `CONNECT_WIDGETS_ENABLED` flag is
// dark, and that exception is mapped to the not-found handler precisely so a dark feature is
// indistinguishable from a route that does not exist. The tab treats it as "not available in this
// environment", never as an error the Owner could act on — see `settings-giving-card-tab.tsx`.
//
// Both calls use `throwOnError: true` so a failure surfaces as the shared `GivmoError` the rest of
// the app's error rendering already understands, rather than a second error convention here.

import { givmoClient } from '@givmo/api-client';

/** The one route this feature talks to. Both verbs are Owner-gated (`company:edit`) server-side. */
export const GIVING_PUBLICATION_URL = '/admin/company/giving-publication';

/**
 * `PUT /admin/company/giving-publication` body.
 *
 * BOTH fields are required, and this is a whole-state PUT rather than a PATCH — CW-BE6's
 * `CompanyGivingPublicationUpdate` models it that way because it is a CONSENT screen: an Owner
 * submitting it restates exactly what may be shown, and a partial update would let a stale client
 * silently keep a block the Owner believed they had removed.
 *
 * `modules` must be NON-EMPTY. "Publish nothing" is spelled `published: false`, never an empty list;
 * the backend refuses the empty spelling rather than admitting a state that reads as published while
 * disclosing nothing. `isSaveableSelection` in `giving-card-decode.ts` is the client-side half of
 * that rule, so the Owner sees why Save is unavailable instead of collecting a 422.
 */
export interface GivingPublicationWriteBody {
  published: boolean;
  modules: string[];
}

/** Read the company's current publication (or its unpublished zero-state). Body is `unknown` — the
 *  response type is not in the snapshot either, so it goes through `decodeGivingPublication`. */
export async function fetchGivingPublication(): Promise<unknown> {
  const { data } = await givmoClient.get({ url: GIVING_PUBLICATION_URL, throwOnError: true });
  return data;
}

/** Publish / unpublish and restate the disclosed blocks. Returns the saved state, same shape. */
export async function saveGivingPublication(body: GivingPublicationWriteBody): Promise<unknown> {
  const { data } = await givmoClient.put({
    url: GIVING_PUBLICATION_URL,
    body,
    throwOnError: true,
  });
  return data;
}
